import { HeidiVersion } from '@heidi/schema/ml-scribe';
import { useAtomValue } from 'jotai';
import { useRef } from 'react';

import {
  HEIDI_2_AWARENESS_BANNER_MODAL_ID,
  HEIDI_2_EVIDENCE_TOUR_SPOTLIGHT_MODAL_ID,
  HEIDI_2_EXPLORE_LATER_BANNER_MODAL_ID,
  HEIDI_2_GET_STARTED_CHECKLIST_MODAL_ID,
} from '../../contracts/heidi-2';

import {
  heidi2ExploreLaterPreviewAtom,
  heidi2ExploreLaterPreviewStartedAtAtom,
} from './heidi-2-awareness-banner-atoms';
import {
  resolveHeidi2WelcomeOutcome,
  type Heidi2ExploreLaterAudience,
} from './heidi-2-explore-later-eligibility';
import { getHeidi2Ports } from './heidi-2-ports';

export interface Heidi2ExploreLaterState extends Heidi2ExploreLaterAudience {
  /** Current-load billing settled (including a terminal error), not restored cache. */
  isBillingReady: boolean;
  isUserManaged: boolean | null | undefined;
  isBannerClosed: boolean;
  isEvidenceTourClosed: boolean;
  isChecklistClosed: boolean;
  /** When the welcome was first seen — or a dev preview started — as an ISO string. */
  welcomeSeenAt: string | undefined;
}

/**
 * Whether any explore-later nudge can show at all: the onboarding flag, or a
 * dev-tools preview. Lets a host keep its plain markup, and skip the queries
 * below, for everyone else.
 */
export const useIsHeidi2ExploreLaterLive = (): boolean => {
  const isHeidi2OnboardingEnabled = getHeidi2Ports().useIsHeidi2OnboardingEnabled();
  const preview = useAtomValue(heidi2ExploreLaterPreviewAtom);
  return isHeidi2OnboardingEnabled || preview !== null;
};

/**
 * Completing the Evidence tour cancels GET_USER, which blanks the projection.
 * Hold the last slice across that gap only. A later successful payload must
 * win as-is — merging missing `seen_modals` rows would leak the previous
 * test account's skip onto the next login.
 */
const rememberHeidi2ExploreLaterUser = <T>(
  previous: T | undefined,
  next: T | undefined
): T | undefined => next ?? previous;

/**
 * `getUserBillingInfo` rehydrates from localStorage as success with the
 * previous session's `heidi_version`. The takeover already ignores that
 * window; the Evidence tour must too, or a leftover Heidi 2 + skip paints
 * a spotlight before this account's takeover.
 */
const APP_STARTED_AT = Date.now();

/**
 * Everything the explore-later nudges decide on, read once from the user and
 * billing queries. Reads SaaS billing, so only mount callers on cloud builds —
 * the same reason `Heidi2AwarenessBanner` skips on-prem.
 */
export const useHeidi2ExploreLaterState = (): Heidi2ExploreLaterState => {
  const ports = getHeidi2Ports();
  const isHeidi2OnboardingEnabled = ports.useIsHeidi2OnboardingEnabled();
  const {
    data: billingInfo,
    isPending: isBillingInfoPending,
    isError: isBillingInfoError,
    isFetching: isBillingInfoFetching,
    dataUpdatedAt: billingInfoUpdatedAt,
  } = ports.useUserBillingInfo();
  const hasFreshBillingInfo = billingInfoUpdatedAt >= APP_STARTED_AT;
  const { data: user } = ports.useQueryUser((user) =>
    user
      ? {
          isManaged: user.is_managed,
          welcomeView: user.seen_modals?.[HEIDI_2_AWARENESS_BANNER_MODAL_ID],
          bannerView: user.seen_modals?.[HEIDI_2_EXPLORE_LATER_BANNER_MODAL_ID],
          tourView: user.seen_modals?.[HEIDI_2_EVIDENCE_TOUR_SPOTLIGHT_MODAL_ID],
          checklistView: user.seen_modals?.[HEIDI_2_GET_STARTED_CHECKLIST_MODAL_ID],
        }
      : undefined
  );
  const lastUserRef = useRef(user);
  lastUserRef.current = rememberHeidi2ExploreLaterUser(lastUserRef.current, user);
  const stableUser = lastUserRef.current;

  const preview = useAtomValue(heidi2ExploreLaterPreviewAtom);
  const previewStartedAt = useAtomValue(heidi2ExploreLaterPreviewStartedAtAtom);
  const seenModalIds = ports.useSeenModalIds();
  const audience = preview
    ? {
        isHeidi2OnboardingEnabled: true,
        isHeidi2: true,
        welcomeOutcome: preview,
        isUserManaged: false,
      }
    : {
        isHeidi2OnboardingEnabled,
        isHeidi2:
          hasFreshBillingInfo && billingInfo?.heidi_version === HeidiVersion.Value2,
        welcomeOutcome: resolveHeidi2WelcomeOutcome(stableUser?.welcomeView),
        isUserManaged: stableUser?.isManaged,
      };

  return {
    ...audience,
    // A background refetch keeps a fresh answer settled; only an in-flight
    // retry after an error, or a restored-from-storage payload, stays pending.
    isBillingReady: isBillingInfoError
      ? !isBillingInfoFetching
      : !!billingInfo && !isBillingInfoPending && hasFreshBillingInfo,
    isBannerClosed: ports.isModalClosed(
      HEIDI_2_EXPLORE_LATER_BANNER_MODAL_ID,
      stableUser?.bannerView
    ),
    // The queue's seen set is written the moment the tour closes and survives
    // a rejected save, so the Home dot goes with the tour, not a refresh later.
    isEvidenceTourClosed:
      seenModalIds?.has(HEIDI_2_EVIDENCE_TOUR_SPOTLIGHT_MODAL_ID) === true ||
      ports.isModalClosed(HEIDI_2_EVIDENCE_TOUR_SPOTLIGHT_MODAL_ID, stableUser?.tourView),
    isChecklistClosed: ports.isModalClosed(
      HEIDI_2_GET_STARTED_CHECKLIST_MODAL_ID,
      stableUser?.checklistView
    ),
    // `first_seen_at` is optional on the record; without an anchor the
    // checklist would show a task that can never tick.
    welcomeSeenAt: preview
      ? (previewStartedAt ?? undefined)
      : (stableUser?.welcomeView?.first_seen_at ?? stableUser?.welcomeView?.last_seen_at),
  };
};
